import React, { useState } from "react";
import { createPageUrl } from "@/utils";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ReferenceValue } from "@/entities/all";

const COL = {
  value:   'flex-1 min-w-0',
  desc:    'w-[200px] shrink-0',
  syn:     'w-[110px] shrink-0',
  status:  'w-[70px]  shrink-0',
  created: 'w-[100px] shrink-0',
  actions: 'w-[50px]  shrink-0',
};

function SynonymTag({ label }) {
  return (
    <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-full min-w-0"
      style={{ background: 'rgba(127,199,249,0.20)', color: '#377AD6' }}
      title={label}>
      {label}
    </span>
  );
}

export default function ReferenceValuesTable({ values, synonyms, isLoading, onValueUpdate, getValueSynonyms, listId, onDeleteValue }) {
  const navigate = useNavigate();
  const [updatingValues, setUpdatingValues] = useState(new Set());

  const handleToggleActive = async (value) => {
    const valueId = value.id;
    setUpdatingValues(prev => new Set([...prev, valueId]));
    try {
      await ReferenceValue.update(valueId, { is_active: !value.is_active });
      onValueUpdate();
    } catch (error) {
      console.error('Error updating value status:', error);
    } finally {
      setUpdatingValues(prev => { const next = new Set(prev); next.delete(valueId); return next; });
    }
  };

  if (!isLoading && values.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-sm text-page-muted">No reference values found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-[820px]">
      {/* Header */}
      <div className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
        style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}>
        <div className={`${COL.value} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Canonical value</div>
        <div className={`${COL.desc} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Description</div>
        <div className={`${COL.syn} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Synonyms</div>
        <div className={`${COL.status} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Status</div>
        <div className={`${COL.created} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Created</div>
        <div className={COL.actions} />
      </div>

      {/* Rows */}
      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center h-11 px-5 gap-[10px]" style={{ background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--table-stripe)' }}>
            <Skeleton className="h-4 w-40" />
          </div>
        ))
      ) : (
        values.map((value, i) => {
          const valueSynonyms = getValueSynonyms(value.id);
          const approvedSynonyms = valueSynonyms.filter(s => s.is_approved);
          const isUpdating = updatingValues.has(value.id);
          const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';

          return (
            <div
              key={value.id}
              className={`flex items-center h-11 px-5 gap-[10px] cursor-pointer ${!value.is_active ? 'opacity-60' : ''}`}
              style={{ background: rowBg }}
              onClick={() => navigate(createPageUrl(`reference-lists/${value.list_id}/values/${value.id}`))}
            >
              <div className={COL.value}>
                <span className="text-sm font-medium truncate block" style={{ color: '#377AD6' }}>
                  {value.canonical_value}
                </span>
              </div>
              <div className={COL.desc}>
                <span className="text-sm truncate block" style={{ color: 'var(--page-text)', fontWeight: 400 }}>
                  {value.value_description || <span style={{ color: 'var(--page-muted)' }}>No description</span>}
                </span>
              </div>
              <div className={`${COL.syn} flex items-center gap-1 min-w-0`}>
                {approvedSynonyms.length > 0 ? (
                  <>
                    <SynonymTag label={approvedSynonyms[0].synonym} />
                    {approvedSynonyms.length > 1 && (
                      <span className="text-xs shrink-0" style={{ color: 'var(--page-muted)' }}>+{approvedSynonyms.length - 1}</span>
                    )}
                  </>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--page-muted)' }}>—</span>
                )}
              </div>
              <div className={`${COL.status} flex items-center`} onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={value.is_active}
                  onCheckedChange={() => handleToggleActive(value)}
                  disabled={isUpdating}
                  size="sm"
                  title={value.is_active ? 'Active' : 'Inactive'}
                />
              </div>
              <div className={COL.created}>
                <span className="text-sm" style={{ color: 'var(--page-text)', fontWeight: 400 }}>
                  {format(new Date(value.created_date), 'MMM d, yyyy')}
                </span>
              </div>
              <div className={`${COL.actions} flex justify-end`} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onDeleteValue(value)}
                  className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
